import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getPropertyById, getRelatedProperties } from '../data/properties';

const featureIcons = {
  pool: 'pool',
  parking: 'local_parking',
  wifi: 'wifi',
  aircon: 'ac_unit',
  kitchen: 'kitchen',
  washer: 'local_laundry_service',
  seaView: 'waves',
  terrace: 'deck',
  garden: 'yard',
  bbq: 'outdoor_grill',
};

const PropertyDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  
  const rawProperty = getPropertyById(id);
  const rawRelated = getRelatedProperties(id, 3);

  const property = rawProperty ? {
    ...rawProperty,
    name: t(rawProperty.nameKey),
    location: t(rawProperty.locationKey),
    description: t(rawProperty.descKey),
  } : null;

  const relatedProperties = rawRelated.map(p => ({
    ...p,
    name: t(p.nameKey),
    location: t(p.locationKey),
    description: t(p.descKey),
  }));

  const getBadgeLabel = (badge) => {
    const badges = {
      popular: t('common.popular'),
      new: t('common.new'),
      luxury: t('common.luxury'),
      seaView: t('common.seaView'),
      beachfront: t('common.beachfront'),
      center: t('common.center'),
    };
    return badges[badge] || badge;
  };

  const getFeatureLabel = (feature) => {
    return t(`features.${feature}`) || feature;
  };

  if (!property) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">{t('properties.noPropertiesTitle')}</h1>
          <p className="text-white/60 mb-8">{t('properties.noPropertiesText')}</p>
          <Link to="/properties" className="bg-primary text-black px-6 py-3 rounded-lg font-bold">
            {t('common.viewAll')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-dark text-white min-h-screen pt-20">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-8">
          <Link to="/" className="text-white/60 hover:text-primary transition-colors">{t('nav.home')}</Link>
          <span className="text-white/40">/</span>
          <Link to="/properties" className="text-white/60 hover:text-primary transition-colors">{t('nav.properties')}</Link>
          <span className="text-white/40">/</span>
          <span className="text-primary">{property.name}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Left Column - Images */}
          <div className="w-full lg:w-[60%] space-y-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden group shadow-2xl"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" 
                style={{ backgroundImage: `url(${property.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute top-6 left-6">
                <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold ${property.badgeColor}`}>
                  {getBadgeLabel(property.badge)}
                </span>
              </div>
            </motion.div>

            <div className="bg-surface-dark rounded-2xl p-6 md:p-8 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-4">{t('propertyDetail.aboutProperty')}</h2>
              <p className="text-white/70 leading-relaxed">{property.description}</p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <h3 className="text-lg font-bold text-white mb-4">{t('propertyDetail.keyFeatures')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 text-white/70">
                      <span className="material-symbols-outlined text-primary text-lg">{featureIcons[feature] || 'check_circle'}</span>
                      <span>{getFeatureLabel(feature)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="w-full lg:w-[40%]">
            <div className="sticky top-24 space-y-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-surface-dark rounded-2xl p-6 md:p-8 shadow-2xl border border-white/10"
              >
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">{property.name}</h1>
                <div className="flex items-center gap-2 text-white/60 mb-6">
                  <span className="material-symbols-outlined text-lg">location_on</span>
                  <span>{property.location}</span>
                </div>
                <div className="mb-6 pb-6 border-b border-white/10">
                  <p className="text-sm text-white/50 uppercase tracking-wider mb-1">{t('propertyDetail.price')}</p>
                  <div className="text-3xl md:text-4xl font-bold text-primary">{property.pricePerNight}<span className="text-lg text-white/50 font-normal"> {t('common.perNight')}</span></div>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-white/5 p-4 rounded-xl text-center border border-white/10">
                    <span className="material-symbols-outlined text-primary text-2xl mb-2">bed</span>
                    <div className="font-bold text-white text-xl">{property.beds}</div>
                    <div className="text-xs text-white/50 uppercase font-semibold">{t('common.beds')}</div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl text-center border border-white/10">
                    <span className="material-symbols-outlined text-primary text-2xl mb-2">bathtub</span>
                    <div className="font-bold text-white text-xl">{property.baths}</div>
                    <div className="text-xs text-white/50 uppercase font-semibold">{t('common.baths')}</div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl text-center border border-white/10">
                    <span className="material-symbols-outlined text-primary text-2xl mb-2">group</span>
                    <div className="font-bold text-white text-xl">{property.guests}</div>
                    <div className="text-xs text-white/50 uppercase font-semibold">{t('common.guests')}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <Link to="/contact" className="w-full bg-primary hover:bg-primary-hover text-black font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined">calendar_month</span>
                    <span>{t('propertyDetail.bookNow')}</span>
                  </Link>
                  <a href="https://wa.me/385911234567" className="w-full bg-white/10 hover:bg-white/20 text-white font-bold py-4 rounded-xl transition-all border border-white/10 flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined">chat</span>
                    <span>{t('propertyDetail.messageWhatsApp')}</span>
                  </a>
                </div>
              </motion.div>

              <div className="bg-surface-dark rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4">{t('propertyDetail.needHelpTitle')}</h3>
                <p className="text-white/60 text-sm mb-4">{t('propertyDetail.needHelpText')}</p>
                <div className="space-y-3">
                  <a href="tel:+385911234567" className="flex items-center gap-3 text-white/70 hover:text-primary transition-colors">
                    <span className="material-symbols-outlined text-primary">call</span>
                    <span>{t('common.phone')}</span>
                  </a>
                  <a href="mailto:info@splitapartmani.hr" className="flex items-center gap-3 text-white/70 hover:text-primary transition-colors">
                    <span className="material-symbols-outlined text-primary">mail</span>
                    <span>{t('common.email')}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Properties */}
        <section className="mt-20 pt-16 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('propertyDetail.similarTitle')}</h2>
              <p className="text-white/60">{t('propertyDetail.similarSubtitle')}</p>
            </div>
            <Link to="/properties" className="inline-flex items-center gap-2 text-primary hover:text-white transition-colors font-bold group">
              {t('common.viewAll')}
              <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {relatedProperties.map((prop, index) => (
              <motion.article 
                key={prop.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-surface-dark rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-white/10 hover:border-primary/30"
              >
                <Link to={`/properties/${prop.id}`}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url("${prop.image}")` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-bold ${prop.badgeColor} tracking-wide uppercase shadow-sm`}>{getBadgeLabel(prop.badge)}</span>
                    </div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <div className="text-2xl font-bold text-primary">{prop.pricePerNight}<span className="text-sm text-white/70 font-normal"> {t('common.perNight')}</span></div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-white text-xl font-bold mb-2 group-hover:text-primary transition-colors">{prop.name}</h3>
                    <p className="text-white/60 text-sm mb-4 flex items-center gap-1">
                      <span className="material-symbols-outlined text-base">location_on</span>
                      {prop.location}
                    </p>
                    <div className="flex items-center justify-between py-4 border-t border-white/10">
                      <div className="flex items-center gap-2 text-white/60">
                        <span className="material-symbols-outlined text-lg">bed</span>
                        <span className="text-sm font-medium">{prop.beds}</span>
                      </div>
                      <div className="w-px h-6 bg-white/10" />
                      <div className="flex items-center gap-2 text-white/60">
                        <span className="material-symbols-outlined text-lg">bathtub</span>
                        <span className="text-sm font-medium">{prop.baths}</span>
                      </div>
                      <div className="w-px h-6 bg-white/10" />
                      <div className="flex items-center gap-2 text-white/60">
                        <span className="material-symbols-outlined text-lg">group</span>
                        <span className="text-sm font-medium">{prop.guests}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PropertyDetailPage;
